"use client"

import { useState, useEffect } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { useTheme } from "../../contexts/ThemeContext"
import { useAuth } from "../../contexts/AuthContext"
import {
  HomeIcon,
  ChartBarIcon,
  ChatBubbleLeftIcon,
  DocumentTextIcon,
  ChartPieIcon,
  UsersIcon,
  KeyIcon,
  QuestionMarkCircleIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  DevicePhoneMobileIcon,
} from "@heroicons/react/24/outline"

const Sidebar = ({ collapsed, mobileOpen, onClose }) => {
  const { isDark } = useTheme()
  const navigate = useNavigate()
  const location = useLocation()
  const { user, getSupportNotifications, effectiveLogo } = useAuth()
  const [expandedItems, setExpandedItems] = useState({ dashboard: true })
  const [isHovered, setIsHovered] = useState(false)
  const [unreadCount, setUnreadCount] = useState(0)

  useEffect(() => {
    if (!user) return

    const loadUnreadCount = async () => {
      try {
        if (!getSupportNotifications) return
        const res = await getSupportNotifications(true) // Get unread notifications only
        if (res.success) {
          setUnreadCount(res.unreadCount || 0)
        }
      } catch (err) {
        console.error("Error loading support unread count in Sidebar:", err)
      }
    }

    loadUnreadCount()

    // Refresh count every 30 seconds
    const interval = setInterval(loadUnreadCount, 30000)
    return () => clearInterval(interval)
  }, [user, getSupportNotifications])

  const toggleExpanded = (item) => {
    setExpandedItems({ [item]: !expandedItems[item] })
  }

  const isAdmin = ["superadmin", "admin"].includes(user?.role)

  // Default full menu for non-admin users
  // Build dashboard submenu conditionally based on operator and role
  const dashboardSubmenu =
    user?.role === "reseller"
      ? [
          { label: "Reseller Dashboard", route: "/dashreseller" },
          { label: "User Accounts", route: "/user-accounts" },
        ]
      : [{ label: "Main Dashboard", route: "/dashusers" }]

  if (user?.operator === "cloud" && !["agent", "reseller"].includes(user?.role)) {
    dashboardSubmenu.push({ label: "Waba Dashboard", route: "/waba-dashboard" })
  }
  if (user?.role !== "reseller") {
    dashboardSubmenu.push({ label: "User Accounts", route: "/user-accounts" })
  }

  const defaultMenuItems = [
    {
      id: "dashboard",
      label: "Dashboard",
      icon: HomeIcon,
      hasSubmenu: true,
      submenu: dashboardSubmenu,
    },
    { id: "single-campaign", label: "Single Campaign", icon: ChartBarIcon, route: "/single-campaign" },
    { id: "campaign", label: "Campaign", icon: ChartBarIcon, route: "/campaign" },
    { id: "chat", label: "Chat", icon: ChatBubbleLeftIcon, route: "/chat" },
    { id: "bot", label: "Bot", icon: ChatBubbleLeftIcon, route: "/botFlow" },
    {
      id: "templates",
      label: "Templates",
      icon: DocumentTextIcon,
      hasSubmenu: true,
      submenu:
        user?.role === "agent"
          ? [{ label: "All Templates", route: "/templates" }]
          : [
              { label: "All Templates", route: "/templates" },
              { label: "Create Template", route: "/create-template" },
            ],
    },
    {
      id: "reports",
      label: "Reports",
      icon: ChartPieIcon,
      hasSubmenu: true,
      submenu: [
        { label: "Campaign Report", route: "/campaign-report" },
        { label: "API Report", route: "/api-report" },
        { label: "Conversation Report", route: "/conversation-report" },
        { label: "Campaign Conversational Report", route: "/campaign-conversational-report" },
      ],
    },
    {
      id: "contacts",
      label: "Contacts",
      icon: UsersIcon,
      hasSubmenu: true,
      submenu: [
        { label: "Contact", route: "/contact" },
        { label: "Group", route: "/group" },
      ],
    },
  ]

  const defaultOtherItems = [
    { id: "api", label: "API Key", icon: KeyIcon, route: "/api-key" },
    { id: "support", label: "Support", icon: QuestionMarkCircleIcon, route: "/support" },
  ]

  const adminDashboardRoute = user?.role === "superadmin" ? "/su-admin" : "/dash-admin"
  const adminDashboardLabel = user?.role === "superadmin" ? "SuperAdmin Dashboard" : "Admin Dashboard"

  const adminDashboardSubmenu = [
    { label: adminDashboardLabel, route: adminDashboardRoute },
    { label: "User Accounts", route: "/user-accounts" },
  ]

  // Restricted menu for superadmin/admin as requested
  const adminMenuItems = [
    {
      id: "dashboard",
      label: "Dashboard",
      icon: HomeIcon,
      hasSubmenu: true,
      submenu: adminDashboardSubmenu,
    },
    { id: "user-account", label: "User Account", icon: UsersIcon, route: "/user-accounts" },
    { id: "meta", label: "Meta", icon: DocumentTextIcon, route: "/meta" },
    { id: "whatsapp-signup", label: "WhatsApp Signup", icon: DevicePhoneMobileIcon, route: "/whatsapp-signup" },
    {
      id: "templates",
      label: "Templates",
      icon: DocumentTextIcon,
      hasSubmenu: true,
      submenu: [{ label: "All Templates", route: "/templates" }],
    },
    {
      id: "campaign-history",
      label: "Campaign History",
      icon: ChartBarIcon,
      route: "/campaign-history",
    },
    {
      id: "queue",
      label: "Queue Messages",
      icon: ChartBarIcon,
      route: "/queue",
    },
    {
      id: "reports",
      label: "Reports",
      icon: ChartPieIcon,
      hasSubmenu: true,
      submenu: [
        { label: "Campaign Report", route: "/campaign-report" },
        { label: "API Report", route: "/api-report" },
        { label: "Conversation Report", route: "/conversation-report" },
        { label: "Campaign Conversational Report", route: "/campaign-conversational-report" },
      ],
    },
    {
      id: "contacts",
      label: "Contacts",
      icon: UsersIcon,
      hasSubmenu: true,
      submenu: [
        { label: "Contact", route: "/contact" },
        { label: "Group", route: "/group" },
      ],
    },
  ]

  const adminOtherItems = [
    { id: "api", label: "API Key", icon: KeyIcon, route: "/api-key" },
    { id: "support", label: "Support", icon: QuestionMarkCircleIcon, route: "/support" },
  ]

  const supportMenuItems = [
    {
      id: "queue",
      label: "Queue Messages",
      icon: ChartBarIcon,
      route: "/queue",
    },
    {
      id: "campaign-history",
      label: "Campaign History",
      icon: ChartBarIcon,
      route: "/campaign-history",
    },
  ]

  const supportOtherItems = []

  const resellerMenuItems = [
    {
      id: "dashboard",
      label: "Dashboard",
      icon: HomeIcon,
      hasSubmenu: true,
      submenu: [
        { label: "Reseller Dashboard", route: "/dashreseller" },
        { label: "User Accounts", route: "/user-accounts" },
      ],
    },
    { id: "user-account", label: "User Account", icon: UsersIcon, route: "/user-accounts" },
    { id: "meta", label: "Meta Pricing", icon: DocumentTextIcon, route: "/meta" },
    { id: "whatsapp-signup", label: "WhatsApp Signup", icon: DevicePhoneMobileIcon, route: "/whatsapp-signup" },
    {
      id: "templates",
      label: "Templates",
      icon: DocumentTextIcon,
      hasSubmenu: true,
      submenu: [{ label: "All Templates", route: "/templates" }],
    },
    {
      id: "reports",
      label: "Reports",
      icon: ChartPieIcon,
      hasSubmenu: true,
      submenu: [
        { label: "Campaign Report", route: "/campaign-report" },
        { label: "API Report", route: "/api-report" },
        { label: "Conversation Report", route: "/conversation-report" },
      ],
    },
    {
      id: "contacts",
      label: "Contacts",
      icon: UsersIcon,
      hasSubmenu: true,
      submenu: [
        { label: "Contact", route: "/contact" },
        { label: "Group", route: "/group" },
      ],
    },
  ]

  const rawMenuItems = user?.role === "support"
    ? supportMenuItems
    : user?.role === "reseller"
    ? resellerMenuItems
    : (isAdmin ? adminMenuItems : defaultMenuItems)

  const menuItems = rawMenuItems.filter(item => {
    if (item.id === "bot") {
      return !!user?.bot
    }
    if (["superadmin", "admin", "reseller", "company"].includes(user?.role)) {
      if (["single-campaign", "campaign", "chat", "bot"].includes(item.id)) {
        return false
      }
    }
    return true
  })

  const otherItems = user?.role === "support"
    ? supportOtherItems
    : (isAdmin ? adminOtherItems : defaultOtherItems)

  const handleNavigation = (route, isProfile = false) => {
    if (isProfile && user?._id) {
      navigate(`/userprofile/${user._id}`)
    } else {
      navigate(route)
    }
    if (mobileOpen) onClose()
  }

  const isRouteActive = (route) => {
    return location.pathname === route
  }

  const MenuItem = ({ item }) => (
    <div className="mb-1 relative">
      <button
        onClick={() => {
          if (item.hasSubmenu) {
            if ((collapsed && !isHovered) && item.submenu?.[0]) {
              handleNavigation(item.submenu[0].route)
            } else {
              toggleExpanded(item.id)
            }
          } else if (item.route) {
            handleNavigation(item.route, item.isProfile)
          }
        }}
        className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-2xl transition-all duration-300 
          ${(collapsed && !isHovered) ? "justify-center" : "justify-between"} 
          text-black dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800`}
        style={{
          background:
            item.submenu?.some((sub) => isRouteActive(sub.route)) ||
            (item.route && isRouteActive(item.route)) ||
            expandedItems[item.id]
              ? "linear-gradient(135deg, rgba(74, 124, 89, 0.1), rgba(255, 215, 0, 0.1))"
              : "transparent",
        }}
      >
        <div className="flex items-center w-full">
          <div className="relative">
            <item.icon className="w-5 h-5 flex-shrink-0 transition-colors duration-300" />
            {(collapsed && !isHovered) && item.id === "support" && unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full border border-white dark:border-gray-900" />
            )}
          </div>
          {(!collapsed || isHovered) && (
            <div className="flex items-center justify-between w-full ml-3">
              <span className="font-semibold">{item.label}</span>
              {item.id === "support" && unreadCount > 0 && (
                <span className="px-2 py-0.5 text-xs font-bold bg-red-500 text-white rounded-full">
                  {unreadCount}
                </span>
              )}
            </div>
          )}
        </div>
        {(!collapsed || isHovered) &&
          item.hasSubmenu &&
          (expandedItems[item.id] ? (
            <ChevronDownIcon className="w-4 h-4 text-gray-600 dark:text-gray-400 transition-transform duration-300" />
          ) : (
            <ChevronRightIcon className="w-4 h-4 text-gray-600 dark:text-gray-400 transition-transform duration-300" />
          ))}
      </button>

      {(!collapsed || isHovered) && item.hasSubmenu && expandedItems[item.id] && (
        <div className="ml-8 mt-2 space-y-1 animate-in slide-in-from-top-2 duration-300">
          {item.submenu?.map((subItem, index) => (
            <button
              key={index}
              onClick={() => handleNavigation(subItem.route)}
              className={`w-full text-left px-4 py-2.5 text-sm rounded-xl transition-all duration-300 font-medium hover:bg-gray-100 dark:hover:bg-gray-800 ${
                isRouteActive(subItem.route)
                  ? "text-black dark:text-white font-bold shadow-sm"
                  : "text-black dark:text-white"
              }`}
              style={{
                background: isRouteActive(subItem.route)
                  ? "linear-gradient(135deg, rgba(74, 124, 89, 0.15), rgba(255, 215, 0, 0.15))"
                  : "transparent",
              }}
            >
              {subItem.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )

  return (
    <>
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      <div
        className={`
          fixed lg:static inset-y-0 left-0 z-50 
          ${(collapsed && !isHovered) ? "w-16" : "w-64"} 
          ${mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
          bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl border-r border-gray-200/50 dark:border-gray-800/50
          transition-all duration-300 ease-in-out
          flex flex-col shadow-2x2
        `}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="flex items-center px-4 py-6 border-b border-gray-200/50 dark:border-gray-800/50 min-h-[64px]">
          <div className="flex items-center justify-center w-full">
            {effectiveLogo ? (
              <img
                src={effectiveLogo}
                alt="Logo"
                className={`${(collapsed && !isHovered) ? "h-8 max-w-[40px]" : "h-10 max-w-[180px]"} object-contain transition-all duration-300`}
              />
            ) : (
              <img
                src={isDark ? "/assests/logo/logodark.png" : "/assests/logo/logo.png"}
                alt="Logo"
                className={`${(collapsed && !isHovered) ? "h-8 max-w-[40px]" : "h-10 max-w-[180px]"} object-contain transition-all duration-300`}
              />
            )}
          </div>
        </div>

        <div className="flex-1 px-3 py-6 overflow-y-auto">
          {(!collapsed || isHovered) && (
            <div className="text-xs font-bold text-black dark:text-white uppercase tracking-wider mb-4 px-3">
              MENU
            </div>
          )}

          <div className="space-y-2">
            {menuItems.map((item) => (
              <MenuItem key={item.id} item={item} />
            ))}
          </div>

          {(!collapsed || isHovered) && (
            <div className="text-xs font-bold text-black dark:text-white uppercase tracking-wider mb-4 mt-8 px-3">
              OTHERS
            </div>
          )}

          <div className="space-y-2">
            {otherItems.map((item) => (
              <MenuItem key={item.id} item={item} />
            ))}
          </div>
        </div>
      </div>
    </>
  )
}

export default Sidebar
